import { namespace } from 'wsm-tagging-utils';

const runWidgetUserEventDecorator = (win) => {
	namespace(win, 'DDC.tracking.adobe_launch.decorators');

	const {
		DDC: { tracking }
	} = win;
	// window.DDC = window.DDC || {};
	// window.DDC.client = window.DDC.client || {};
	// window.DDC.client.isMobile = window.DDC.client.isMobile || false;

	/**
	 * The Widget User Event Decorator sets the proper values for the section on eventData.
	 * These properties combine to create the widget user event property returned in many events.
	 *
	 * The section describes the section of the page that the event occurred on.
	 *
	 * @param {Object} window The window object
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @param {tracking.framework.runtime.EventHandler} delegate The EventHandler implementation to which all interface operations are delegated
	 * @constructor
	 */
	tracking.adobe_launch.decorators.WidgetUserEventDecorator = class extends (
		tracking.framework.runtime.EventHandlerDecorator
	) {
		constructor(thisWin, page, delegate) {
			super(thisWin, page, delegate);
			this.logCategory = 'WidgetUserEventDecorator';
			this.utils = new tracking.adobe_launch.util.DecoratorUtils();
			this.getStringForElement = this.utils.getStringForElement;
			this.getFirstWord = this.utils.getFirstWord;
			this.parseStringValue = this.utils.parseStringValue;
			this.replaceObjectValues = this.utils.replaceObjectValues;

			/**
			 * Map of widget section name to selector that should define that section.
			 */
			this.widgetSectionSelectors = new Map([
				['Global Nav', '.page-header,.navbar,.mycars-default']
			]);

			/**
			 * Map of form category to selector that should define that form category.
			 */
			this.widgetFormCategorySelectors = new Map([
				['other', '.mycars-default form']
			]);

			/**
			 * Map of form type to selector that should define that form type.
			 */
			this.widgetFormTypeSelectors = new Map([
				['other', '.mycars-default form']
			]);

			// Handlers needing decorator applied
			this.eventHandlers = ['handleWidgetUserEvent'];

			/**
			 * Map of properties to match by widget in order to specify which data should be put into eventdata
			 */
			this.widgetUserEventMap = {
				'ws-site-text-search': [
					{
						eventDataProperty: 'widgetId',
						equals: 'template-site-search1',
						additionalData: {
							section: 'Global Nav',
							formName: 'ws-site-text-search',
							activeElementName: 'search'
						}
					},
					{
						eventDataProperty: 'widgetId',
						equals: 'site-search1',
						additionalData: {
							section: 'site-search1',
							formName: 'ws-site-text-search',
							activeElementName: 'search'
						}
					}
				],
				'ws-inv-text-search': [
					{
						eventDataProperty: 'widgetId',
						equals: 'inventory-search1',
						additionalData: {
							section: 'inventory-search1',
							formName: 'ws-inv-text-search',
							activeElementName: 'search'
						}
					}
				],
				'ws-inv-facets': [
					{
						eventDataProperty: 'element',
						includes: 'accordion',
						additionalData: {
							linkType: 'text.link',
							url: '#',
							text: this.getFirstWord('element'),
							handler: 'handleLinkClick'
						}
					},
					{
						eventDataProperty: 'widgetId',
						includes: 'inventory-facets1',
						additionalData: {
							section: 'inventory-facets1',
							handler: 'handleLinkClick'
						}
					},
					{
						eventDataProperty: 'result',
						includes: 'collapsed',
						additionalData: {
							expand: 'false',
							handler: 'handleLinkClick'
						}
					},
					{
						eventDataProperty: 'result',
						includes: 'expanded',
						additionalData: {
							expand: 'true',
							handler: 'handleLinkClick'
						}
					}
				],
				'ws-vehicle-reccomendations': [
					{
						eventDataProperty: 'element',
						includes: 'fivestar',
						additionalData: {
							linkType: 'text.link',
							url: '#',
							handler: 'handleLinkClick'
						}
					}
				],
				'ws-dealer-reviews': [
					{
						eventDataProperty: 'element',
						includes: 'Carousel',
						additionalData: {
							// eslint-disable-next-line quotes
							interactionValue: `{currentItem}/{totalItems}`,
							// eslint-disable-next-line quotes
							label: `{label}`,
							// eslint-disable-next-line quotes
							text: `{label}`,
							linkType: 'button.link',
							section: 'What Our Customers Say',
							handler: 'handleLinkClick'
						}
					},
					{
						eventDataProperty: 'element',
						includes: 'dots',
						additionalData: {
							// eslint-disable-next-line quotes
							interactionValue: `{currentItem}/{totalItems}`,
							// eslint-disable-next-line quotes
							label: `{label}`,
							// eslint-disable-next-line quotes
							text: `page {currentItem} bullet`,
							linkType: 'button.link',
							section: 'What Our Customers Say',
							handler: 'handleLinkClick'
						}
					}
				],
				'ws-content-cta': [
					{
						eventDataProperty: 'element',
						includes: 'Call to action',
						additionalData: {
							section: 'pagecontent',
							element: 'pagecontent',
							url: '{url}',
							linkType: 'image.link',
							text: '{ariaLabel}',
							handler: 'handleLinkClick'
						}
					}
				],
				'model-selector': [
					{
						eventDataProperty: 'element',
						includes: 'Model category filter',
						additionalData: {
							section: 'pagecontent',
							element: 'pagecontent',
							url: '#',
							linkType: 'text.link',
							text: '{text}',
							handler: 'handleLinkClick'
						}
					}
				]
			};

			this.getNewEntriesForWidget = (eventData, newObject, item) => {
				const { eventDataProperty, includes, additionalData, equals } =
					item;
				const additionalDataCopy = { ...additionalData };
				const itIncludes =
					includes &&
					eventData[eventDataProperty] &&
					eventData[eventDataProperty].includes(includes);
				const itEquals =
					equals &&
					eventData[eventDataProperty] &&
					eventData[eventDataProperty] === equals;
				if (itIncludes || itEquals) {
					const eventDataValues = Object.assign(
						eventData,
						eventData.additionalData || eventData.optionalData
					);
					Object.assign(
						newObject,
						this.replaceObjectValues(
							additionalDataCopy,
							eventDataValues
						)
					);
				}
				return newObject;
			};

			this.getNewEventData = function getNewEventData(
				eventData,
				widgetMap
			) {
				const newEventData =
					(widgetMap &&
						widgetMap.reduce(
							this.getNewEntriesForWidget.bind(null, eventData),
							{}
						)) ||
					{};
				return Object.assign(eventData, newEventData);
			};
			this.createHandlerDecorators();
		}

		// Create handler decorators from eventHandler array to add eventData.section and update eventData.text
		createHandlerDecorators() {
			this.eventHandlers.forEach((handler) => {
				let defaultHandler = handler;
				this[handler] = (eventDataIn) => {
					let eventData = eventDataIn;
					const optionalData = eventData && eventData.optionalData;
					// eslint-disable-next-line max-len
					const myCarsTrackingId =
						optionalData &&
						optionalData.element &&
						optionalData.element
							.closest('[data-mycars-form-tracking-id]')
							.getAttribute('data-mycars-form-tracking-id');
					if (myCarsTrackingId) {
						const {
							optionalData: { element, trackingId },
							section: eventDataSection
						} = eventData;
						let section;
						if (!eventDataSection) {
							section = this.getStringForElement(
								element,
								this.widgetSectionSelectors
							);
							// Replacing possible invisible control codes such as null or newline character with an empty space test
							/* eslint-disable */
							eventData.section =
								section &&
								section.replace(/[\x00-\x1F\x7F-\x9F]/g, ' ') &&
								section.trim();
							/* eslint-enable */
							this.log.debug(
								`setting section of widget user event to: ${section}`
							);
						}

						if (!trackingId) {
							try {
								eventData.trackingId = myCarsTrackingId;
							} catch (e) {
								this.log.debug('no trackingId found for event');
							}
						}

						eventData.formCategory = this.getStringForElement(
							element,
							this.widgetFormCategorySelectors
						);
						eventData.formType = this.getStringForElement(
							element,
							this.widgetFormTypeSelectors
						);
					} else {
						const widgetMap =
							this.widgetUserEventMap[eventData.widgetName];
						if (widgetMap) {
							eventData = this.getNewEventData(
								eventData,
								widgetMap
							);
							if (eventData.handler) {
								defaultHandler = eventData.handler;
							}
						}
					}
					this.delegate[defaultHandler](eventData);
				};
			});
		}
	};

	tracking.adobe_launch.decorators.WidgetUserEventDecorator.prototype.logCategory =
		'WidgetUserEventDecorator';
};

export default runWidgetUserEventDecorator;
